import { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faWallet,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import {
  paymentService,
  formatCurrency,
  formatShortDate,
  formatApiError,
} from "../../../../../services/api";
import { useAuth } from "../../../../../context/authContext";
import {
  Button,
  Input,
  Modal,
  ErrorBanner,
} from "../../../../../components/ui";
import IdentityVerificationModal from "../../../../../components/identity/IdentityVerificationModal";
import { BANK_CHOICES, isWalletBank, bankNameFromId } from "../../../../../data/banks";
import {
  validateWallet,
  validateForKind,
  validateAccountNumber,
} from "../../../../../utils/brebKeyValidator";
import {
  loadSavedWallets,
  saveWallet,
  deleteWallet,
  maskAccount,
} from "../../../../../utils/savedPayoutWallets";
import styles from "./desembolsosPage.module.css";

const KEY_KINDS = [
  { id: "phone", label: "Celular" },
  { id: "email", label: "Correo" },
  { id: "document", label: "Cédula" },
  { id: "alias", label: "Alias" },
];

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function formatAmountInput(digits) {
  if (!digits) return "";
  return new Intl.NumberFormat("es-CO").format(Number(digits));
}

function Chip({ selected, onClick, children }) {
  return (
    <button
      type="button"
      className={`${styles.chip} ${selected ? styles.chipSelected : ""}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export default function Desembolsos({ onGoToDatos }) {
  const { user, profile, isAuthenticated, loadProfile } = useAuth();
  const userId = user?.id || profile?.id;
  const firstName = profile?.first_name || user?.first_name || "";
  const lastName = profile?.last_name || user?.last_name || "";
  const identification = profile?.identification || user?.identification || "";
  const balance = user?.balance?.amount ?? user?.balance ?? profile?.balance?.amount;
  // Solo bloquea en el front cuando el perfil dice explícitamente "no verificado";
  // el backend sigue siendo la fuente de verdad (GL-USR-013).
  const isUnverified =
    (profile?.is_identity_verified ?? user?.is_identity_verified) === false;

  const [bankId, setBankId] = useState("");
  const [destinationKind, setDestinationKind] = useState("");
  const [brebKeyKind, setBrebKeyKind] = useState("");
  const [accountType, setAccountType] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [amountDigits, setAmountDigits] = useState("");
  const [saveAccount, setSaveAccount] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [selectedWalletId, setSelectedWalletId] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [items, setItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");

  const [incompleteOpen, setIncompleteOpen] = useState(false);
  const [overLimit, setOverLimit] = useState(null);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [identityMessage, setIdentityMessage] = useState("");
  const [verifyOpen, setVerifyOpen] = useState(false);

  const walletBank = isWalletBank(bankId);

  useEffect(() => {
    if (userId) setWallets(loadSavedWallets(userId));
  }, [userId]);

  const loadHistory = useCallback(async () => {
    if (!isAuthenticated) {
      setHistoryLoading(false);
      return;
    }
    setHistoryError("");
    try {
      const result = await paymentService.getWithdrawalHistory();
      if (result.ok) {
        setItems(result.data?.withdrawals || []);
      } else {
        setHistoryError(
          result.message ||
            formatApiError(result.raw) ||
            "No se pudo cargar el historial"
        );
      }
    } catch {
      setHistoryError("Error de conexión");
    } finally {
      setHistoryLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setHistoryLoading(true);
    loadHistory();
  }, [loadHistory]);

  const accountError = useMemo(() => {
    if (!accountNumber.trim() || !bankId) return null;
    if (walletBank) return validateWallet(accountNumber);
    if (destinationKind === "brebKey") {
      if (!brebKeyKind) return "Elige el tipo de llave";
      return validateForKind(accountNumber, brebKeyKind);
    }
    if (destinationKind === "accountNumber") {
      return validateAccountNumber(accountNumber);
    }
    return null;
  }, [accountNumber, bankId, walletBank, destinationKind, brebKeyKind]);

  const accountComplete = useMemo(() => {
    if (!bankId || !accountNumber.trim() || accountError) return false;
    if (walletBank) return true;
    if (destinationKind === "brebKey") return Boolean(brebKeyKind);
    if (destinationKind === "accountNumber") return Boolean(accountType);
    return false;
  }, [
    bankId,
    accountNumber,
    accountError,
    walletBank,
    destinationKind,
    brebKeyKind,
    accountType,
  ]);

  const profileComplete =
    firstName.trim() && lastName.trim() && identification.trim();

  const resetDestination = () => {
    setDestinationKind("");
    setBrebKeyKind("");
    setAccountType("");
    setAccountNumber("");
    setAmountDigits("");
    setSelectedWalletId("");
  };

  const applyWallet = (wallet) => {
    setSelectedWalletId(wallet.id);
    setBankId(wallet.bank_id);
    setDestinationKind(wallet.destination_kind || "brebKey");
    setBrebKeyKind(wallet.key_kind || "");
    setAccountType(wallet.account_type || "");
    setAccountNumber(wallet.account_number || "");
    setSaveAccount(false);
  };

  const handleBankChange = (id) => {
    setBankId(id);
    setSelectedWalletId("");
    setDestinationKind(isWalletBank(id) ? "brebKey" : "");
    setBrebKeyKind("");
    setAccountType("");
    setAccountNumber("");
    setAmountDigits("");
  };

  const persistWalletIfNeeded = () => {
    if (!saveAccount || !userId || !bankId) return;
    const wallet = {
      id: `${Date.now()}`,
      label: bankNameFromId(bankId),
      bank_id: bankId,
      bank_name: bankNameFromId(bankId),
      destination_kind: walletBank ? "brebKey" : destinationKind,
      key_kind: brebKeyKind || null,
      account_type: walletBank || destinationKind === "brebKey" ? "DIGITAL" : accountType,
      account_number: accountNumber.trim(),
      use_keys: walletBank || destinationKind === "brebKey",
      created_at: new Date().toISOString(),
    };
    setWallets(saveWallet(userId, wallet));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSuccessMessage("");

    if (!profileComplete) {
      setIncompleteOpen(true);
      return;
    }
    if (isUnverified) {
      setIdentityMessage(
        "Para solicitar un retiro primero debes verificar tu identidad con las fotos de tu cédula."
      );
      setIdentityOpen(true);
      return;
    }
    if (!bankId) {
      setFormError("Selecciona un banco");
      return;
    }
    if (!accountComplete) {
      setFormError(accountError || "Completa los datos de la cuenta");
      return;
    }
    const amount = Number(amountDigits);
    if (!amountDigits || !Number.isFinite(amount) || amount <= 0) {
      setFormError("Ingresa el valor a retirar");
      return;
    }

    const useKeys = walletBank || destinationKind === "brebKey";
    setSubmitting(true);
    try {
      const result = await paymentService.requestWithdrawal({
        user: userId,
        amount,
        bank: bankId,
        account_type: useKeys ? "DIGITAL" : accountType,
        account_number: accountNumber.trim(),
        use_keys: useKeys,
      });

      if (result.ok) {
        persistWalletIfNeeded();
        setSuccessMessage("Solicitud de retiro enviada correctamente");
        resetDestination();
        setBankId("");
        setSaveAccount(false);
        await loadHistory();
        if (typeof loadProfile === "function") await loadProfile();
        return;
      }

      if (result.code === "GL-PAY-014") {
        setOverLimit({
          message: result.message,
          supportContact: result.supportContact,
        });
        return;
      }
      if (result.code === "identity_not_verified") {
        setIdentityMessage(
          result.message ||
            "Debes verificar tu identidad para solicitar un retiro."
        );
        setIdentityOpen(true);
        return;
      }
      setFormError(result.message || "No se pudo solicitar el retiro");
    } catch {
      setFormError("Error de conexión");
    } finally {
      setSubmitting(false);
    }
  };

  const support = overLimit?.supportContact || {};
  const supportPhone = String(support.phone || "").replace(/\D/g, "") || "3138572510";
  const supportEmail = support.email || "";
  const supportHours = support.hours || "";

  return (
    <div className={styles.container}>
      <div className={styles.balanceRow}>
        <div>
          <p className={styles.balanceLabel}>Saldo disponible</p>
          <p className={styles.balanceValue}>{formatCurrency(balance || 0)}</p>
        </div>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => {
            setHistoryLoading(true);
            loadHistory();
          }}
          title="Actualizar"
          aria-label="Actualizar historial"
        >
          <FontAwesomeIcon icon={faSync} />
        </button>
      </div>

      {isUnverified ? (
        <div className={styles.identityNotice}>
          <p className={styles.identityNoticeText}>
            Para retirar tus premios primero debes verificar tu identidad con
            las fotos de tu cédula.
          </p>
          <Button size="sm" onClick={() => setVerifyOpen(true)}>
            Verificar mi identidad
          </Button>
        </div>
      ) : null}

      <ul className={styles.rules}>
        <li>Puedes retirar el 100% del dinero ganado, con un mínimo de $50.000 COP.</li>
        <li>La cuenta o billetera debe estar activa y a tu nombre.</li>
        <li>La transferencia puede tardar hasta 72 horas hábiles.</li>
        <li>
          WhatsApp premios:{" "}
          <a href="https://wa.me/3138572510" target="_blank" rel="noopener noreferrer">
            313 857 2510
          </a>
        </li>
      </ul>

      {wallets.length > 0 ? (
        <div className={styles.wallets}>
          <p className={styles.sectionLabel}>Cuentas guardadas</p>
          <div className={styles.walletRow}>
            {wallets.map((wallet) => (
              <div
                key={wallet.id}
                className={`${styles.walletCard} ${
                  selectedWalletId === wallet.id ? styles.walletCardSelected : ""
                }`}
              >
                <button
                  type="button"
                  className={styles.walletMain}
                  onClick={() => applyWallet(wallet)}
                >
                  <FontAwesomeIcon icon={faWallet} />
                  <span>
                    {wallet.bank_name || bankNameFromId(wallet.bank_id)}
                    <small>{maskAccount(wallet.account_number)}</small>
                  </span>
                </button>
                <button
                  type="button"
                  className={styles.walletDelete}
                  aria-label="Eliminar cuenta"
                  onClick={() => {
                    if (window.confirm("¿Eliminar esta cuenta guardada?")) {
                      setWallets(deleteWallet(userId, wallet.id));
                      if (selectedWalletId === wallet.id) resetDestination();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <form className={styles.form} onSubmit={handleSubmit}>
        <label className={styles.fieldLabel} htmlFor="retiro-banco">
          Banco o billetera
        </label>
        <select
          id="retiro-banco"
          className={styles.select}
          value={bankId}
          onChange={(e) => handleBankChange(e.target.value)}
        >
          <option value="">Selecciona</option>
          {BANK_CHOICES.map((bank) => (
            <option key={bank.id} value={bank.id}>
              {bank.name}
            </option>
          ))}
        </select>

        {bankId && !walletBank ? (
          <>
            <p className={styles.fieldLabel}>Cómo recibes</p>
            <div className={styles.chipRow}>
              <Chip
                selected={destinationKind === "brebKey"}
                onClick={() => {
                  setDestinationKind("brebKey");
                  setAccountType("");
                  setAccountNumber("");
                }}
              >
                Llave Bre-b
              </Chip>
              <Chip
                selected={destinationKind === "accountNumber"}
                onClick={() => {
                  setDestinationKind("accountNumber");
                  setBrebKeyKind("");
                  setAccountNumber("");
                }}
              >
                Número de cuenta
              </Chip>
            </div>
          </>
        ) : null}

        {bankId && !walletBank && destinationKind === "brebKey" ? (
          <>
            <p className={styles.fieldLabel}>Tipo de llave</p>
            <div className={styles.chipRow}>
              {KEY_KINDS.map((kind) => (
                <Chip
                  key={kind.id}
                  selected={brebKeyKind === kind.id}
                  onClick={() => setBrebKeyKind(kind.id)}
                >
                  {kind.label}
                </Chip>
              ))}
            </div>
          </>
        ) : null}

        {bankId && (walletBank || destinationKind) ? (
          <Input
            id="retiro-cuenta"
            label={
              walletBank || destinationKind === "brebKey"
                ? "Llave Bre-b"
                : "Número de cuenta"
            }
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            error={accountNumber ? accountError : undefined}
            autoComplete="off"
          />
        ) : null}

        {bankId && !walletBank && destinationKind === "accountNumber" ? (
          <>
            <label className={styles.fieldLabel} htmlFor="retiro-tipo">
              Tipo de cuenta
            </label>
            <select
              id="retiro-tipo"
              className={styles.select}
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
            >
              <option value="">Selecciona</option>
              <option value="SAVINGS">Cuenta de Ahorros</option>
              <option value="CHECKING">Cuenta Corriente</option>
            </select>
          </>
        ) : null}

        {accountComplete ? (
          <Input
            id="retiro-monto"
            label="Valor a retirar"
            inputMode="numeric"
            value={formatAmountInput(amountDigits)}
            onChange={(e) => setAmountDigits(digitsOnly(e.target.value))}
            hint="Mínimo $50.000 COP"
            placeholder="$ 50.000"
          />
        ) : null}

        {accountComplete ? (
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={saveAccount}
              onChange={(e) => setSaveAccount(e.target.checked)}
            />
            Guardar esta cuenta para próximos retiros
          </label>
        ) : null}

        {formError ? <ErrorBanner message={formError} /> : null}
        {successMessage ? (
          <p className={styles.success}>{successMessage}</p>
        ) : null}

        <Button type="submit" fullWidth loading={submitting} disabled={submitting}>
          Solicitar retiro
        </Button>
      </form>

      <h3 className={styles.historyTitle}>Historial de retiros</h3>
      {historyLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p>Cargando retiros...</p>
        </div>
      ) : historyError ? (
        <ErrorBanner message={historyError} onRetry={loadHistory} />
      ) : items.length === 0 ? (
        <p className={styles.emptyText}>Aún no tienes retiros</p>
      ) : (
        <div className={styles.list}>
          {items.map((item) => {
            const status = String(item.status || "").toUpperCase();
            return (
              <div key={item.id || item.withdrawal_code} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.amount}>
                    {formatCurrency(item.amount)}
                  </span>
                  <span
                    className={`${styles.statusBadge} ${
                      styles[
                        status === "APPROVED"
                          ? "statusApproved"
                          : status === "PENDING"
                            ? "statusPending"
                            : "statusMuted"
                      ]
                    }`}
                  >
                    {item.status_display || status}
                  </span>
                </div>
                <div className={styles.cardMeta}>
                  <p>
                    <span className={styles.metaLabel}>Destino:</span>{" "}
                    {item.bank_display || item.bank}
                  </p>
                  {item.account_number ? (
                    <p>
                      <span className={styles.metaLabel}>Cuenta:</span>{" "}
                      {maskAccount(item.account_number)}
                    </p>
                  ) : null}
                  {item.withdrawal_code ? (
                    <p>
                      <span className={styles.metaLabel}>Código:</span>{" "}
                      {item.withdrawal_code}
                    </p>
                  ) : null}
                  {item.created_at ? (
                    <p>
                      <span className={styles.metaLabel}>Fecha:</span>{" "}
                      {formatShortDate(item.created_at)}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={incompleteOpen}
        onClose={() => setIncompleteOpen(false)}
        title="Perfil incompleto"
      >
        <p className={styles.modalText}>
          Para solicitar un retiro necesitas tener nombre, apellido e
          identificación registrados en tu perfil.
        </p>
        <div className={styles.modalActions}>
          <Button variant="secondary" onClick={() => setIncompleteOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setIncompleteOpen(false);
              onGoToDatos?.();
            }}
          >
            Ir a mis datos
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(overLimit)}
        onClose={() => setOverLimit(null)}
        title="Retiro manual requerido"
      >
        <p className={styles.modalText}>{overLimit?.message}</p>
        {supportEmail ? <p className={styles.modalText}>Correo: {supportEmail}</p> : null}
        {support.phone ? <p className={styles.modalText}>Teléfono: {support.phone}</p> : null}
        {supportHours ? <p className={styles.modalText}>Horario: {supportHours}</p> : null}
        {!supportEmail && !support.phone ? (
          <p className={styles.modalText}>WhatsApp premios: 313 857 2510</p>
        ) : null}
        <div className={styles.modalActions}>
          <Button variant="secondary" onClick={() => setOverLimit(null)}>
            Cerrar
          </Button>
          <Button
            onClick={() => {
              if (supportPhone) {
                window.open(`https://wa.me/${supportPhone}`, "_blank", "noopener,noreferrer");
              } else if (supportEmail) {
                window.location.href = `mailto:${supportEmail}`;
              }
            }}
          >
            Contactar
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={identityOpen}
        onClose={() => setIdentityOpen(false)}
        title="Verificación requerida"
      >
        <p className={styles.modalText}>{identityMessage}</p>
        <div className={styles.modalActions}>
          <Button variant="secondary" onClick={() => setIdentityOpen(false)}>
            Más tarde
          </Button>
          <Button
            onClick={() => {
              setIdentityOpen(false);
              setVerifyOpen(true);
            }}
          >
            Verificar mi identidad
          </Button>
        </div>
      </Modal>

      <IdentityVerificationModal
        isOpen={verifyOpen}
        onClose={() => setVerifyOpen(false)}
      />
    </div>
  );
}
